import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Banknote, FileText, ArrowLeft } from "lucide-react";
import { expertises } from "@/data/expertises";
import ContactSection from "@/components/ContactSection";

export default function Expertises() {
  const { hash } = useLocation();

  // scroll to hash target after mount / hash change
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    }
  }, [hash]);

  return (
    <div data-testid="expertises-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-50 text-slate-900">
        <div className="absolute inset-0 bg-grid-soft opacity-[0.06] pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full bg-[#10B981]/18 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#0055A4]/12 blur-3xl" />

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-40 md:pt-48 pb-24 md:pb-32">
          <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Nos 14 expertises</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-[-0.025em] text-balance leading-[1.02] max-w-4xl">
            Cliquez sur une expertise. Découvrez les travaux d&apos;optimisation.
          </h1>
          <p className="mt-7 text-base md:text-xl text-slate-600 max-w-2xl leading-relaxed">
            Pour chaque domaine technique, ECONATION identifie les opérations concrètes
            finançables par les CEE — avec les fiches d&apos;opération standardisées et une
            fourchette de valorisation typique.
          </p>

          {/* Anchor grid */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2">
            {expertises.map((e) => (
              <a
                key={e.slug}
                href={`#${e.slug}`}
                data-testid={`expertise-anchor-${e.slug}`}
                className="group px-3 py-2.5 rounded-lg bg-slate-50 hover:bg-[#10B981]/15 border border-slate-200 hover:border-[#10B981]/50 backdrop-blur-md text-slate-700 hover:text-slate-900 text-[11px] font-medium transition-all flex items-center gap-2"
              >
                <e.icon className="h-3.5 w-3.5 text-[#10B981]" strokeWidth={1.75} />
                <span className="truncate">{e.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* DETAIL SECTIONS */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 md:py-24 space-y-16 md:space-y-24">
          {expertises.map((e, idx) => (
            <article
              key={e.slug}
              id={e.slug}
              data-testid={`expertise-section-${e.slug}`}
              className="scroll-mt-28 grid lg:grid-cols-12 gap-10 items-start"
            >
              {/* Left: title / meta */}
              <div className="lg:col-span-5 lg:sticky lg:top-28">
                <div className="text-[11px] font-mono text-[#94A3B8] mb-4">{String(idx + 1).padStart(2, "0")} / 14</div>
                <div className="h-14 w-14 rounded-2xl bg-[#064E3B] grid place-items-center text-[#10B981] mb-5">
                  <e.icon className="h-6 w-6" strokeWidth={1.75} />
                </div>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight text-balance leading-[1.05]">
                  {e.label}
                </h2>
                <p className="mt-4 text-lg text-[#475569] leading-relaxed">{e.tagline}</p>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-black/[0.06] bg-[#F8FAFC] p-4">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#94A3B8] mb-1.5">
                      <FileText className="h-3.5 w-3.5" /> Fiche CEE
                    </div>
                    <div className="font-display text-sm font-semibold text-[#0F172A]">{e.fiche}</div>
                  </div>
                  <div className="rounded-xl border border-[#10B981]/25 bg-[#10B981]/[0.06] p-4">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#10B981] font-semibold mb-1.5">
                      <Banknote className="h-3.5 w-3.5" /> Valorisation
                    </div>
                    <div className="font-display text-sm font-semibold text-[#064E3B]">{e.valorisation}</div>
                  </div>
                </div>

                <Link to="/eligibilite" data-testid={`expertise-cta-${e.slug}`}>
                  <Button className="mt-6 bg-[#0055A4] hover:bg-[#003D7A] text-slate-900 font-semibold py-5 px-7 rounded-full shadow-[0_18px_40px_-16px_rgba(249,115,22,0.6)]">
                    Auditer mon projet <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>

              {/* Right: works list */}
              <div className="lg:col-span-7">
                <div className="rounded-3xl border border-black/[0.06] bg-gradient-to-br from-white to-[#F8FAFC] p-8 md:p-10">
                  <div className="text-[11px] uppercase tracking-[0.24em] text-[#10B981] font-semibold mb-5">Travaux d&apos;optimisation finançables</div>
                  <ul className="space-y-4">
                    {e.works.map((w, i) => (
                      <li
                        key={i}
                        data-testid={`expertise-${e.slug}-work-${i + 1}`}
                        className="group flex items-start gap-4 pb-4 border-b border-black/[0.05] last:border-b-0 last:pb-0"
                      >
                        <span className="mt-0.5 h-6 w-6 rounded-lg bg-[#10B981]/10 grid place-items-center text-[#10B981] group-hover:bg-[#10B981] group-hover:text-slate-900 transition-colors shrink-0">
                          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
                        </span>
                        <span className="text-[#0F172A] leading-relaxed">{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}

          <div className="pt-10 flex justify-center">
            <Link to="/solutions" data-testid="expertises-back-solutions">
              <Button variant="outline" className="border-2 border-[#0F172A] text-[#0F172A] hover:bg-white hover:text-slate-900 rounded-full py-6 px-8">
                <ArrowLeft className="mr-2 h-4 w-4" /> Retour aux solutions
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <ContactSection />
    </div>
  );
}
